import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Terminal, AlertTriangle, Loader2 } from 'lucide-react';
interface ForensicTerminalOverlayProps {
  status: 'idle' | 'pending' | 'processing' | 'completed' | 'failed';
  mode: string;
  onTimeout?: () => void;
}
const getForensicLogs = (mode: string, status: string) => {
  const modeUpper = mode.toUpperCase();
  const logs: Record<string, string[]> = {
    pending: ['[SYS] Initializing forensic analysis module...', '[NET] Establishing secure handshake...', '[AUTH] Verifying user credentials...', '[SYS] Handshake established.', `[SYS] ANALYZING MODE: ${modeUpper}`, '[DATA] Loading conversation dataset...', '[DATA] Extracting text patterns...', '[DATA] TRIANGULATING PATTERNS...', '[PROC] Queuing analysis request...'],
    processing: [`[AI] Running ${modeUpper} analysis protocols...`, '[DATA] SCANNING FOR TOXICITY...', '[DATA] TRIANGULATING PATTERNS...', '[SCAN] Identifying manipulation markers...', '[AI] Detecting gaslighting patterns...', '[PROC] Cross-referencing behavioral signatures...', '[AI] Computing risk assessment scores...', '[DATA] Analyzing emotional manipulation tactics...', `[SCAN] Applying ${modeUpper}-specific detection rules...`, '[AI] Evaluating communication dynamics...', '[PROC] Generating forensic report...'],
    completed: ['[AI] Analysis complete.', '[DATA] Report generated successfully.', '[SYS] Finalizing results...'],
    failed: ['[ERROR] Analysis failed.', '[SYS] Check logs for details.', '[SYS] Terminating process...']
  };
  return logs[status] || [];
};
export function ForensicTerminalOverlay({
  status,
  mode,
  onTimeout
}: ForensicTerminalOverlayProps) {
  const [displayedLogs, setDisplayedLogs] = useState<string[]>([]);
  const [currentLogIndex, setCurrentLogIndex] = useState(0);
  const [showTimeout, setShowTimeout] = useState(false);
  // Timeout detection
  useEffect(() => {
    if (status === 'pending' || status === 'processing') {
      const timeoutTimer = setTimeout(() => {
        setShowTimeout(true);
        if (onTimeout) onTimeout();
      }, 30000); // 30 seconds
      return () => clearTimeout(timeoutTimer);
    }
  }, [status, onTimeout]);
  // Log animation
  useEffect(() => {
    const logs = getForensicLogs(mode, status);
    if (currentLogIndex < logs.length) {
      const timer = setTimeout(() => {
        setDisplayedLogs(prev => [...prev, logs[currentLogIndex]]);
        setCurrentLogIndex(prev => prev + 1);
      }, 400 + Math.random() * 300); // Randomized timing for realism
      return () => clearTimeout(timer);
    }
  }, [currentLogIndex, status, mode]);
  // Reset on status change
  useEffect(() => {
    // Don't reset if moving from pending to processing as it looks jarring
    if (status === 'processing' && displayedLogs.length > 0) {
      // Just append new logs
      setCurrentLogIndex(0);
    } else {
      setDisplayedLogs([]);
      setCurrentLogIndex(0);
      setShowTimeout(false);
    }
  }, [status]);
  // Only show overlay during active states
  if (status === 'idle' || status === 'completed' || status === 'failed') {
    return null;
  }
  return <div className="fixed inset-0 z-50 bg-black flex items-center justify-center" data-id="element-3336">
      {/* Scanline effect */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-3337">
        <motion.div className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#00FF00] to-transparent opacity-30" animate={{
        top: ['0%', '100%']
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-3338" />
      </div>

      {/* CRT screen effect */}
      <div className="absolute inset-0 bg-[repeating-linear-gradient(0deg,rgba(0,255,0,0.03)_0px,transparent_1px,transparent_2px,rgba(0,255,0,0.03)_3px)] pointer-events-none" data-id="element-3339" />

      <div className="max-w-3xl w-full mx-4 relative" data-id="element-3340">
        {/* Terminal Window */}
        <div className="bg-black border-2 border-[#00FF00] shadow-[0_0_30px_rgba(0,255,0,0.3)] relative overflow-hidden" data-id="element-3341">
          {/* Terminal Header */}
          <div className="bg-[#001a00] border-b-2 border-[#00FF00] px-4 py-2 flex items-center gap-3" data-id="element-3342">
            <Terminal size={16} className="text-[#00FF00]" data-id="element-3343" />
            <span className="text-[#00FF00] font-mono text-sm uppercase tracking-wider" data-id="element-3344">
              FORENSIC ANALYSIS TERMINAL v2.1.4
            </span>
            <div className="ml-auto flex gap-2" data-id="element-3345">
              <div className="w-3 h-3 rounded-full bg-[#00FF00] animate-pulse" data-id="element-3346" />
            </div>
          </div>

          {/* Terminal Body */}
          <div className="p-6 min-h-[400px] max-h-[500px] overflow-y-auto font-mono text-sm" data-id="element-3347">
            <div className="space-y-2" data-id="element-3348">
              {/* Mode indicator */}
              <div className="text-[#00FF00] mb-4 pb-4 border-b border-[#00FF00]/30" data-id="element-3349">
                <span className="opacity-60" data-id="element-3350">[MODE]</span> {mode.toUpperCase()}
              </div>

              {/* Scrolling logs */}
              <AnimatePresence mode="popLayout" data-id="element-3351">
                {displayedLogs.map((log, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.2
              }} className="leading-relaxed text-[#00FF00]" data-id="element-3352">
                    <span className="opacity-60 mr-2" data-id="element-3353">{'>'}</span>
                    {log}
                  </motion.div>)}
              </AnimatePresence>

              {/* Timeout warning */}
              {showTimeout && <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="mt-6 p-4 border-2 border-amber-500 bg-amber-500/10" data-id="element-3354">
                  <div className="flex items-start gap-3" data-id="element-3355">
                    <AlertTriangle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" data-id="element-3356" />
                    <div className="text-amber-500 font-mono text-sm space-y-1" data-id="element-3357">
                      <div data-id="element-3358">[WARNING] Analysis taking longer than expected.</div>
                      <div data-id="element-3359">[SYS] Processing in background...</div>
                      <div className="opacity-60 text-xs mt-2" data-id="element-3360">
                        Your scan is queued. You can wait here or check
                        dashboard later.
                      </div>
                    </div>
                  </div>
                </motion.div>}

              {/* Cursor blink */}
              <motion.div className="inline-block w-2 h-4 bg-[#00FF00] ml-1" animate={{
              opacity: [1, 0]
            }} transition={{
              duration: 0.8,
              repeat: Infinity
            }} data-id="element-3361" />
            </div>
          </div>

          {/* Terminal Footer */}
          <div className="bg-[#001a00] border-t-2 border-[#00FF00] px-4 py-2" data-id="element-3362">
            <div className="flex items-center justify-between text-[#00FF00] font-mono text-xs" data-id="element-3363">
              <span className="opacity-60" data-id="element-3364">STATUS: {status.toUpperCase()}</span>
              <span className="opacity-60" data-id="element-3365">
                {new Date().toLocaleTimeString('en-US', {
                hour12: false
              })}
              </span>
            </div>
          </div>
        </div>

        {/* Glow effect */}
        <div className="absolute inset-0 bg-[#00FF00] opacity-5 blur-3xl pointer-events-none" data-id="element-3366" />
      </div>
    </div>;
}